import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Users, Search, UserCheck, UserX, Loader2, Calendar, 
  Crown, Mail, Phone, RefreshCw, Eye, CheckCircle2, XCircle,
  Clock, CreditCard, Image, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrganizers = () => {
  const [organizers, setOrganizers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialogs
  const [viewOrganizerOpen, setViewOrganizerOpen] = useState(false);
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);
  const [viewSubscriptionOpen, setViewSubscriptionOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [organizerToDelete, setOrganizerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orgRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/organizers`),
        axios.get(`${API_URL}/api/admin/organizer-subscriptions`)
      ]);
      setOrganizers(orgRes.data);
      setSubscriptions(subRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load organizers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (organizerId) => {
    try {
      await axios.put(`${API_URL}/api/admin/organizers/${organizerId}/status`);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleApprove = async () => {
    if (!selectedSubscription) return;
    setProcessing(true);
    try {
      await axios.put(`${API_URL}/api/admin/organizer-subscriptions/${selectedSubscription.id}/approve`);
      toast.success('Subscription approved');
      setViewSubscriptionOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSubscription || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/organizer-subscriptions/${selectedSubscription.id}/reject?reason=${encodeURIComponent(rejectionReason)}`
      );
      toast.success('Subscription rejected');
      setRejectDialogOpen(false);
      setViewSubscriptionOpen(false);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteOrganizer = async () => {
    if (!organizerToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/organizers/${organizerToDelete.id}`);
      toast.success(`Organizer ${organizerToDelete.email} deleted successfully`);
      setDeleteDialogOpen(false);
      setOrganizerToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organizer');
    } finally {
      setDeleting(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSubscriptionStatus = (organizerId) => {
    const sub = subscriptions.find(s => s.organizer_id === organizerId);
    if (!sub) return { status: 'free', badge: <Badge className="bg-gray-600 text-white">Free</Badge> };
    
    switch (sub.status) {
      case 'approved':
        return { 
          status: 'approved', 
          badge: <Badge className="bg-green-500/20 text-green-400"><CheckCircle2 className="h-3 w-3 mr-1" />Active</Badge>,
          subscription: sub
        };
      case 'pending':
        return { 
          status: 'pending', 
          badge: <Badge className="bg-amber-500/20 text-amber-400"><Clock className="h-3 w-3 mr-1" />Pending</Badge>,
          subscription: sub
        };
      case 'rejected':
        return { 
          status: 'rejected', 
          badge: <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>,
          subscription: sub
        };
      default:
        return { status: 'free', badge: <Badge className="bg-gray-600 text-white">Free</Badge> };
    }
  };

  const pendingCount = subscriptions.filter(s => s.status === 'pending').length;
  const approvedCount = subscriptions.filter(s => s.status === 'approved').length;

  const filteredOrganizers = organizers.filter(o => {
    const matchesSearch = !searchTerm || 
      o.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'pending') {
      const sub = subscriptions.find(s => s.organizer_id === o.id && s.status === 'pending');
      return matchesSearch && sub;
    }
    if (activeTab === 'subscribed') {
      const sub = subscriptions.find(s => s.organizer_id === o.id && s.status === 'approved');
      return matchesSearch && sub;
    }
    return matchesSearch;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-organizers">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Users className="h-8 w-8" />
              Organizers
            </h1>
            <p className="text-gray-400 mt-1">
              Manage organizer accounts and subscription approvals
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="gap-2 border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <p className="text-sm text-gray-400">Total Organizers</p>
              <p className="text-2xl font-bold text-white">{organizers.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700 relative overflow-hidden">
            <CardContent className="p-4">
              <p className="text-sm text-gray-400">Pending Approval</p>
              <p className="text-2xl font-bold text-amber-400">{pendingCount}</p>
              {pendingCount > 0 && (
                <div className="absolute top-2 right-2">
                  <span className="flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <p className="text-sm text-gray-400">Active Subscriptions</p>
              <p className="text-2xl font-bold text-green-400">{approvedCount}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <p className="text-sm text-gray-400">Total Events</p>
              <p className="text-2xl font-bold text-indigo-400">
                {organizers.reduce((sum, o) => sum + (o.event_count || 0), 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-gray-900">
                  <TabsTrigger value="all" className="data-[state=active]:bg-gray-700">
                    All ({organizers.length})
                  </TabsTrigger>
                  <TabsTrigger value="pending" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                    Pending ({pendingCount})
                  </TabsTrigger>
                  <TabsTrigger value="subscribed" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
                    Subscribed ({approvedCount})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search organizers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700 hover:bg-gray-700/50">
                  <TableHead className="text-gray-400">Organizer</TableHead>
                  <TableHead className="text-gray-400">Contact</TableHead>
                  <TableHead className="text-gray-400">Events</TableHead>
                  <TableHead className="text-gray-400">Subscription</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrganizers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No organizers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrganizers.map((org) => {
                    const subStatus = getSubscriptionStatus(org.id);
                    return (
                      <TableRow key={org.id} className="border-gray-700 hover:bg-gray-700/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-indigo-600 text-white">
                                {getInitials(org.first_name, org.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-white font-medium">{org.first_name} {org.last_name}</p>
                              {org.batch_year && (
                                <p className="text-xs text-gray-500">Batch {org.batch_year}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-gray-300 text-sm flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {org.email}
                            </p>
                            {org.phone && (
                              <p className="text-gray-400 text-xs flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {org.phone}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-white">{org.event_count || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <button 
                            onClick={() => {
                              if (subStatus.subscription) {
                                setSelectedSubscription(subStatus.subscription);
                                setViewSubscriptionOpen(true);
                              }
                            }}
                            className={subStatus.subscription ? "hover:opacity-80 transition-opacity cursor-pointer" : "cursor-default"}
                          >
                            {subStatus.badge}
                          </button>
                        </TableCell>
                        <TableCell>
                          {org.is_active !== false ? (
                            <Badge className="bg-green-600 text-white">Active</Badge>
                          ) : (
                            <Badge className="bg-red-600 text-white">Suspended</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {subStatus.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedSubscription(subStatus.subscription);
                                setViewSubscriptionOpen(true);
                              }}
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                            >
                              <Eye className="h-4 w-4 mr-1" /> Review
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(org.id)}
                            className={org.is_active !== false ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}
                          >
                            {org.is_active !== false ? (
                              <><UserX className="h-4 w-4 mr-1" /> Suspend</>
                            ) : (
                              <><UserCheck className="h-4 w-4 mr-1" /> Activate</>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setOrganizerToDelete(org);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-organizer-${org.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* View Subscription Dialog */}
      <Dialog open={viewSubscriptionOpen} onOpenChange={setViewSubscriptionOpen}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-gold flex items-center gap-2">
              <Crown className="h-5 w-5" />
              Subscription Details
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Review subscription request and payment proof
            </DialogDescription>
          </DialogHeader>

          {selectedSubscription && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Organizer</p>
                  <p className="font-medium">
                    {selectedSubscription.organizer?.first_name} {selectedSubscription.organizer?.last_name}
                  </p>
                  <p className="text-xs text-gray-500">{selectedSubscription.organizer?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Plan</p>
                  <Badge className="bg-indigo-500/20 text-indigo-400">
                    {selectedSubscription.plan?.name || 'Unknown'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Amount</p>
                  <p className="font-bold text-gold text-lg">{formatCurrency(selectedSubscription.amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Payment Method</p>
                  <p className="font-medium uppercase flex items-center gap-1">
                    <CreditCard className="h-4 w-4" />
                    {selectedSubscription.payment_method}
                  </p>
                </div>
              </div>

              {selectedSubscription.reference_number && (
                <div>
                  <p className="text-xs text-gray-500">Reference Number</p>
                  <p className="font-mono bg-gray-800 px-3 py-2 rounded">{selectedSubscription.reference_number}</p>
                </div>
              )}

              {selectedSubscription.payment_proof && (
                <div>
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <Image className="h-3 w-3" /> Payment Proof
                  </p>
                  <img 
                    src={selectedSubscription.payment_proof} 
                    alt="Payment Proof" 
                    className="rounded-lg max-h-48 w-full object-contain bg-gray-800"
                  />
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-gray-800">
                <p className="text-xs text-gray-500">
                  Submitted: {formatDate(selectedSubscription.submitted_at)}
                </p>
                <Badge className={
                  selectedSubscription.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                  selectedSubscription.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  'bg-red-500/20 text-red-400'
                }>
                  {selectedSubscription.status.charAt(0).toUpperCase() + selectedSubscription.status.slice(1)}
                </Badge>
              </div>

              {selectedSubscription.status === 'pending' && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleApprove}
                    disabled={processing}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRejectDialogOpen(true)}
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}

              {selectedSubscription.status === 'rejected' && selectedSubscription.rejection_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs text-red-400 mb-1">Rejection Reason:</p>
                  <p className="text-red-300">{selectedSubscription.rejection_reason}</p>
                </div>
              )}

              {selectedSubscription.status === 'approved' && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <p className="text-xs text-green-400 mb-1">Approved on:</p>
                  <p className="text-green-300">{formatDate(selectedSubscription.approved_at)}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Reject Subscription</DialogTitle>
            <DialogDescription className="text-gray-400">
              Please provide a reason. The organizer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Invalid payment proof, amount mismatch..."
              className="bg-gray-800 border-gray-700 text-white"
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
                className="flex-1 border-gray-700 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Organizer Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Organizer
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This action cannot be undone. All associated data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          {organizerToDelete && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-white font-medium">
                  {organizerToDelete.first_name} {organizerToDelete.last_name}
                </p>
                <p className="text-gray-400 text-sm">{organizerToDelete.email}</p>
                <p className="text-red-400 text-sm mt-2">
                  This will delete: {organizerToDelete.event_count || 0} event(s), all attendees, payments, and related data.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setOrganizerToDelete(null);
                  }}
                  className="flex-1 border-gray-700 text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteOrganizer}
                  disabled={deleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-organizer"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Delete Permanently
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrganizers;
